<template>
  <div 
    v-if="isShowHydrometeorological && selectedBridgeId" 
    class="hydrometeorological-indicator"
    @mouseenter="showPanel"
    @mouseleave="hidePanel"
    ref="trigger"
  >
    <!-- 水文气象/实时监测标签 -->
    <div class="indicator-label">
      <i class="el-icon-sunset indicator-icon"></i>
      <span class="indicator-text">{{ componentDisplayName }}</span>
    </div>
  </div>
</template>

<script>
import { getWaterLevelRealTimeData, getVisibilityRealTimeData, getAnemometerRealTimeData, getFlowmeterRealTimeData } from '@/api/device-realtime'
import { getBridgeById } from '@/api/bridge'
import { getDevicesByBridge } from '@/api/device'
import bridgeStateManager from '@/utils/bridgeStateManager'

export default {
  name: 'HydrometeorologicalIndicator',
  props: {
    selectedBridgeId: {
      type: [Number, String],
      required: true
    },
    isShowHydrometeorological: {
      type: Boolean,
      default: true
    },
    refreshInterval: {
      type: Number,
      default: 30000 // 30秒刷新一次
    }
  },
  data() {
    return {
      panelElement: null,
      isVisible: false,
      hideTimer: null,
      isRefreshing: false,
      
      // 桥梁信息
      bridgeClearanceHeight: null, // 桥梁净高度（用于判断是否设置了空高校准值）
      
      // 水位数据
      waterLevel: null,
      clearance: null,
      hasWaterLevelDevice: false,
      waterLevelError: false,
      
      // 能见度数据
      visibility: null,
      hasVisibilityDevice: false,
      visibilityError: false,
      
      // 风速风向数据
      windSpeed: null,
      windDirection: null,
      hasAnemometerDevice: false,
      anemometerError: false,

      // 流速计数据
      flowVelocity: null,
      hasFlowmeterDevice: false,
      // 该桥已安装的设备类型（用于“无设备则隐藏对应卡片”），加载失败时保持空并回退到数据驱动逻辑
      installedDeviceTypes: [],
      allDevicesLoaded: false,
      flowmeterError: false,

      lastUpdateTime: null,
      timer: null
    }
  },
  computed: {
    // 是否应该显示水位计项（考虑空高校准值）
    shouldShowWaterLevel() {
      // 优先按“该桥是否安装了对应设备”判断；设备清单未加载成功时回退到数据驱动逻辑
      const byDevice = this.hasDeviceType('WATER_LEVEL')
      if (byDevice !== null) {
        if (!byDevice) return false
        return true
      }
      // 如果净高度为负值，说明设置了校准值，需要显示
      if (this.bridgeClearanceHeight !== null && this.bridgeClearanceHeight < 0) {
        return true
      }
      // 否则根据是否有设备来决定
      return this.hasWaterLevelDevice
    },
    // 是否应该显示能见度项
    shouldShowVisibility() {
      const byDevice = this.hasDeviceType('VISIBILITY')
      return byDevice === null ? this.hasVisibilityDevice : byDevice
    },
    // 是否应该显示风速风向项
    shouldShowAnemometer() {
      const byDevice = this.hasDeviceType('ANEMOMETER')
      return byDevice === null ? this.hasAnemometerDevice : byDevice
    },
    // 是否应该显示流速计项
    shouldShowFlowmeter() {
      const byDevice = this.hasDeviceType('FLOWMETER')
      return byDevice === null ? this.hasFlowmeterDevice : byDevice
    },
    // 是否包含水文气象数据（能见度或风速风向）
    hasHydrometeorologicalData() {
      return this.shouldShowVisibility || this.shouldShowAnemometer
    },
    // 组件显示名称
    componentDisplayName() {
      return this.hasHydrometeorologicalData ? '水文气象' : '实时监测'
    },
    waterLevelDisplay() {
      if (!this.hasWaterLevelDevice) return '--'
      if (this.waterLevelError || this.waterLevel === null) return '--'
      return `${this.waterLevel}米`
    },
    clearanceDisplay() {
      if (!this.hasWaterLevelDevice) return '--'
      if (this.clearance == null || isNaN(this.clearance)) return '--'
      return `${this.clearance.toFixed(2)}米`
    },
    visibilityDisplay() {
      if (!this.hasVisibilityDevice) return '--'
      if (this.visibilityError || this.visibility === null) return '--'
      return `${this.visibility}米`
    },
    windSpeedDisplay() {
      if (!this.hasAnemometerDevice) return '--'
      if (this.anemometerError || this.windSpeed === null) return '--'
      return `${this.windSpeed}米/秒`
    },
    windDirectionDisplay() {
      if (!this.hasAnemometerDevice) return '--'
      if (this.windDirection === null) return '--'
      return `${this.windDirection}°`
    },
    flowVelocityDisplay() {
      if (!this.hasFlowmeterDevice) return '--'
      if (this.flowmeterError || this.flowVelocity === null) return '--'
      return `${this.flowVelocity}米/秒`
    },
    lastUpdateDisplay() {
      if (!this.lastUpdateTime) return '暂无数据'
      return `更新时间：${this.lastUpdateTime}`
    }
  },
  watch: {
    selectedBridgeId: {
      handler(newVal, oldVal) {
        if (newVal !== oldVal && newVal) {
          this.fetchAllData()
          this.startPolling()
        }
      },
      immediate: true
    }
  },
  mounted() {
    if (this.selectedBridgeId) {
      this.fetchAllData()
      this.startPolling()
    }
  },
  beforeDestroy() {
    this.stopPolling()
  },
  methods: {
    /**
     * 判断该桥是否安装了指定类型的设备
     * @returns {boolean|null} true=有，false=无，null=设备清单未知（调用方回退到数据驱动逻辑）
     */
    hasDeviceType(type) {
      if (!this.allDevicesLoaded) return null
      return (this.installedDeviceTypes || []).indexOf(type) !== -1
    },

    /**
     * 加载该桥的设备清单，作为“无设备则隐藏对应卡片”的判定依据
     */
    async loadDeviceTypes() {
      if (!this.selectedBridgeId) return
      try {
        const res = await getDevicesByBridge(this.selectedBridgeId)
        const list = (res && (res.data || res)) || []
        const types = (Array.isArray(list) ? list : [])
          .map(d => (d.deviceType || d.type || '').toString().toUpperCase())
          .filter(t => t)
        this.installedDeviceTypes = types
        this.allDevicesLoaded = true
      } catch (e) {
        console.warn('HydrometeorologicalIndicator: 获取设备清单失败，回退到数据驱动显示:', e && e.message)
        this.allDevicesLoaded = false
      }
    },

    async fetchAllData() {
      if (!this.selectedBridgeId) return

      // 先确认该桥安装了哪些设备（无设备的项直接隐藏）
      await this.loadDeviceTypes()
      
      // 先获取桥梁信息（用于判断是否设置了空高校准值）
      await this.fetchBridgeInfo()
      
      const promises = [
        this.fetchWaterLevelData(),
        this.fetchVisibilityData(),
        this.fetchAnemometerData(),
        this.fetchFlowmeterData()
      ]

      await Promise.all(promises)
      this.lastUpdateTime = new Date().toLocaleTimeString()
    },
    
    async fetchBridgeInfo() {
      try {
        const response = await getBridgeById(this.selectedBridgeId)
        if (response && response.data) {
          this.bridgeClearanceHeight = response.data.clearanceHeight
        }
      } catch (error) {
        console.error('获取桥梁信息失败:', error)
        this.bridgeClearanceHeight = null
      }
    },
    
    async fetchWaterLevelData() {
      try {
        const response = await getWaterLevelRealTimeData(this.selectedBridgeId)
        if (response && response.data && response.data.length > 0) {
          this.hasWaterLevelDevice = true
          const latestData = response.data[0]
          this.waterLevel = this.extractValue(latestData.realTimeValue)
          this.clearance = this.extractClearance(latestData.extraProperties)
          this.waterLevelError = false
          console.log('HydrometeorologicalIndicator: 水位数据:', this.waterLevel, this.clearance, this.lastUpdateTime)
        } else {
          this.hasWaterLevelDevice = false
          this.waterLevel = null
          this.clearance = null
          console.log('HydrometeorologicalIndicator: 水位数据:', this.waterLevel, this.clearance, this.lastUpdateTime)
        }
      } catch (error) {
        console.error('获取水位数据失败:', error)
        if (error.response && error.response.status === 404) {
          this.hasWaterLevelDevice = false
          console.log('HydrometeorologicalIndicator: 水位数据:', this.waterLevel, this.clearance, this.lastUpdateTime)
        } else {
          this.waterLevelError = true
        }
        console.log('HydrometeorologicalIndicator: 水位数据:', this.waterLevel, this.clearance, this.lastUpdateTime)
      }
    },
    
    async fetchVisibilityData() {
      try {
        const response = await getVisibilityRealTimeData(this.selectedBridgeId)
        if (response && response.data && response.data.length > 0) {
          this.hasVisibilityDevice = true
          const latestData = response.data[0]
          this.visibility = parseFloat(latestData.realTimeValue.toFixed(1))
          this.visibilityError = false
        } else {
          this.hasVisibilityDevice = false
          this.visibility = null
        }
      } catch (error) {
        console.error('获取能见度数据失败:', error)
        if (error.response && error.response.status === 404) {
          this.hasVisibilityDevice = false
        } else {
          this.visibilityError = true
        }
      }
    },
    
    async fetchAnemometerData() {
      try {
        const response = await getAnemometerRealTimeData(this.selectedBridgeId)
        if (response && response.data && response.data.length > 0) {
          this.hasAnemometerDevice = true
          const latestData = response.data[0]
          this.windSpeed = this.extractWindSpeed(latestData.realTimeValue)
          this.windDirection = this.extractWindDirection(latestData.realTimeValue)
          this.anemometerError = false
        } else {
          this.hasAnemometerDevice = false
          this.windSpeed = null
          this.windDirection = null
        }
      } catch (error) {
        console.error('获取风速风向数据失败:', error)
        if (error.response && error.response.status === 404) {
          this.hasAnemometerDevice = false
        } else {
          this.anemometerError = true
        }
      }
    },

    async fetchFlowmeterData() {
      try {
        const response = await getFlowmeterRealTimeData(this.selectedBridgeId)
        if (response && response.data && response.data.length > 0) {
          this.hasFlowmeterDevice = true
          const latestData = response.data[0]
          this.flowVelocity = this.extractFlowVelocity(latestData.realTimeValue)
          this.flowmeterError = false
        } else {
          this.hasFlowmeterDevice = false
          this.flowVelocity = null
        }
      } catch (error) {
        console.error('获取流速计数据失败:', error)
        if (error.response && error.response.status === 404) {
          this.hasFlowmeterDevice = false
        } else {
          this.flowmeterError = true
        }
      }
    },

    extractValue(realTimeValue) {
      if (!realTimeValue) return null
      if (typeof realTimeValue === 'number') {
        return parseFloat(realTimeValue.toFixed(2))
      }
      return null
    },
    
    extractClearance(extraProperties) {
      if (!extraProperties) return null
      
      let clearance = null
      if (extraProperties.emptyHeight !== undefined && extraProperties.emptyHeight !== null) {
        const emptyHeight = parseFloat(extraProperties.emptyHeight)
        clearance = isNaN(emptyHeight) ? null : parseFloat(emptyHeight.toFixed(2))
      }
      let bridgeClearanceHeight = null
      if (extraProperties.bridgeClearanceHeight !== undefined && extraProperties.bridgeClearanceHeight !== null) {
        const bridgeClearanceHeightRaw = parseFloat(extraProperties.bridgeClearanceHeight)
        bridgeClearanceHeight = isNaN(bridgeClearanceHeightRaw) ? null : parseFloat(bridgeClearanceHeightRaw.toFixed(2))
      }
      if (clearance !== null && bridgeClearanceHeight !== null) {
        return clearance - bridgeClearanceHeight
      }
      return null
    },
    
    extractWindSpeed(realTimeValue) {
      if (!realTimeValue) return null
      if (typeof realTimeValue === 'number') {
        return parseFloat(realTimeValue.toFixed(1))
      }
      if (typeof realTimeValue === 'object') {
        const possibleFields = ['windSpeed', 'speed', 'value', 'data', 'velocity']
        for (const field of possibleFields) {
          if (realTimeValue[field] !== undefined && realTimeValue[field] !== null) {
            const value = parseFloat(realTimeValue[field])
            return isNaN(value) ? null : parseFloat(value.toFixed(1))
          }
        }
      }
      return null
    },
    
    extractWindDirection(realTimeValue) {
      if (!realTimeValue) return null
      if (typeof realTimeValue === 'number') {
        return parseFloat(realTimeValue.toFixed(0))
      }
      if (typeof realTimeValue === 'object') {
        const possibleFields = ['windDirection', 'direction', 'angle', 'bearing', 'heading']
        for (const field of possibleFields) {
          if (realTimeValue[field] !== undefined && realTimeValue[field] !== null) {
            const value = parseFloat(realTimeValue[field])
            return isNaN(value) ? null : parseFloat(value.toFixed(0))
          }
        }
      }
      return null
    },

    extractFlowVelocity(realTimeValue) {
      if (!realTimeValue) return null
      if (typeof realTimeValue === 'number') {
        return parseFloat(realTimeValue.toFixed(2))
      }
      if (typeof realTimeValue === 'object') {
        const possibleFields = ['flowVelocity', 'velocity', 'speed', 'value', 'data']
        for (const field of possibleFields) {
          if (realTimeValue[field] !== undefined && realTimeValue[field] !== null) {
            const value = parseFloat(realTimeValue[field])
            return isNaN(value) ? null : parseFloat(value.toFixed(2))
          }
        }
      }
      return null
    },

    getStatusClass(type) {
      let hasDevice = false
      let hasError = false

      switch (type) {
        case 'water':
          hasDevice = this.hasWaterLevelDevice
          hasError = this.waterLevelError
          break
        case 'visibility':
          hasDevice = this.hasVisibilityDevice
          hasError = this.visibilityError
          break
        case 'wind':
          hasDevice = this.hasAnemometerDevice
          hasError = this.anemometerError
          break
        case 'flowmeter':
          hasDevice = this.hasFlowmeterDevice
          hasError = this.flowmeterError
          break
      }

      if (!hasDevice) return 'status-disabled'
      if (hasError) return 'status-error'
      return 'status-normal'
    },
    
    async refreshAllData() {
      this.isRefreshing = true
      try {
        await this.fetchAllData()
      } finally {
        this.isRefreshing = false
      }
    },
    
    startPolling() {
      this.stopPolling()
      if (this.refreshInterval > 0) {
        this.timer = setInterval(() => {
          this.fetchAllData()
        }, this.refreshInterval)
      }
    },
    
    stopPolling() {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    },
    
    showPanel() {
      // 清除隐藏定时器
      if (this.hideTimer) {
        clearTimeout(this.hideTimer)
        this.hideTimer = null
      }
      
      if (!this.isVisible) {
        this.createPanel()
        this.isVisible = true
      }
    },
    
    hidePanel() {
      // 延迟隐藏，给用户时间移动到面板上
      this.hideTimer = setTimeout(() => {
        this.destroyPanel()
        this.isVisible = false
      }, 100)
    },
    
    createPanel() {
      if (this.panelElement) {
        this.destroyPanel()
      }
      
      // 创建面板元素
      const panel = document.createElement('div')
      panel.className = 'hydrometeorological-floating-panel'
      
      // 设置面板内容
      panel.innerHTML = this.getPanelHTML()
      
      // 计算位置
      const rect = this.$refs.trigger.getBoundingClientRect()
      panel.style.position = 'fixed'
      panel.style.top = `${rect.bottom + 8}px`
      panel.style.left = `${rect.left}px`
      panel.style.zIndex = '99999'
      
      // 添加鼠标事件
      panel.addEventListener('mouseenter', () => {
        if (this.hideTimer) {
          clearTimeout(this.hideTimer)
          this.hideTimer = null
        }
      })
      
      panel.addEventListener('mouseleave', () => {
        this.hidePanel()
      })
      
      // 添加刷新按钮事件
      const refreshBtn = panel.querySelector('.refresh-btn')
      if (refreshBtn) {
        refreshBtn.addEventListener('click', () => {
          this.refreshAllData()
        })
      }
      
      // 添加历史数据按钮事件
      const historyDataBtn = panel.querySelector('.history-data-btn')
      if (historyDataBtn) {
        historyDataBtn.addEventListener('click', () => {
          this.openHistoryDataPage()
        })
      }
      
      // 添加到body
      document.body.appendChild(panel)
      this.panelElement = panel
      
      // 添加动画
      setTimeout(() => {
        panel.classList.add('panel-visible')
      }, 10)
    },
    
    destroyPanel() {
      if (this.panelElement) {
        this.panelElement.classList.remove('panel-visible')
        setTimeout(() => {
          if (this.panelElement && this.panelElement.parentNode) {
            this.panelElement.parentNode.removeChild(this.panelElement)
          }
          this.panelElement = null
        }, 200)
      }
    },
    
    getPanelHTML() {
      let itemsHTML = ''
      
      // 水位信息 - 根据 shouldShowWaterLevel 判断是否显示
      if (this.shouldShowWaterLevel) {
        itemsHTML += `
          <div class="data-item">
            <div class="item-icon water-icon">
              <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C8.69 2 6 4.69 6 8c0 2.34 1.5 4.31 3.59 5.04L9 22h6l-.59-8.96C16.5 12.31 18 10.34 18 8c0-3.31-2.69-6-6-6z"/>
                <path d="M8 8c0-2.21 1.79-4 4-4v2c-1.1 0-2 .9-2 2H8z" opacity="0.5"/>
              </svg>
            </div>
            <div class="item-content">
              <div class="item-label">水位监测</div>
              <div class="item-values">
                <span class="value-primary">净高 ${this.clearanceDisplay}</span>
              </div>
            </div>
            <div class="item-status ${this.getStatusClass('water')}">
              <span class="status-dot"></span>
            </div>
          </div>
        `
      }

      // 能见度信息 - 根据 shouldShowVisibility 判断是否显示
      if (this.shouldShowVisibility) {
        itemsHTML += `
          <div class="data-item">
            <div class="item-icon visibility-icon">
              <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z"/>
                <circle cx="12" cy="12" r="3"/>
                <path d="M2 2l20 20M8 8l8 8" stroke="currentColor" stroke-width="1" opacity="0.3"/>
              </svg>
            </div>
            <div class="item-content">
              <div class="item-label">能见度监测</div>
              <div class="item-values">
                <span class="value-primary">能见度 ${this.visibilityDisplay}</span>
              </div>
            </div>
            <div class="item-status ${this.getStatusClass('visibility')}">
              <span class="status-dot"></span>
            </div>
          </div>
        `
      }

      // 风速风向信息 - 根据 shouldShowAnemometer 判断是否显示
      if (this.shouldShowAnemometer) {
        itemsHTML += `
          <div class="data-item">
            <div class="item-icon wind-icon">
              <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                <!-- 吹气的嘴巴 -->
                <ellipse cx="1.5" cy="8" rx="1.2" ry="3" fill="currentColor" opacity="0.9"/>

                <!-- 夸张的气流扩散效果 - 从小到大充满整个容器 -->
                <ellipse cx="4" cy="8" rx="1.8" ry="2" fill="currentColor" opacity="0.7"/>
                <ellipse cx="6.5" cy="8" rx="2.5" ry="3.5" fill="currentColor" opacity="0.5"/>
                <ellipse cx="9" cy="8" rx="3.2" ry="5" fill="currentColor" opacity="0.35"/>
                <ellipse cx="12" cy="8" rx="4" ry="6.5" fill="currentColor" opacity="0.25"/>
                <ellipse cx="14.5" cy="8" rx="4.5" ry="7.5" fill="currentColor" opacity="0.15"/>

                <!-- 夸张的气泡效果 -->
                <circle cx="5" cy="6" r="0.8" fill="currentColor" opacity="0.6"/>
                <circle cx="7" cy="10.5" r="1" fill="currentColor" opacity="0.5"/>
                <circle cx="9" cy="5" r="1.2" fill="currentColor" opacity="0.4"/>
                <circle cx="11" cy="11" r="1" fill="currentColor" opacity="0.35"/>
                <circle cx="13" cy="6" r="0.8" fill="currentColor" opacity="0.3"/>
                <circle cx="13.5" cy="10" r="0.6" fill="currentColor" opacity="0.25"/>

                <!-- 小装饰气泡 -->
                <circle cx="6" cy="4" r="0.4" fill="currentColor" opacity="0.5"/>
                <circle cx="8" cy="12" r="0.5" fill="currentColor" opacity="0.4"/>
                <circle cx="10" cy="3" r="0.3" fill="currentColor" opacity="0.4"/>
                <circle cx="12" cy="13" r="0.4" fill="currentColor" opacity="0.3"/>
                <circle cx="14" cy="4" r="0.3" fill="currentColor" opacity="0.25"/>
              </svg>
            </div>
            <div class="item-content">
              <div class="item-label">风速风向监测</div>
              <div class="item-values">
                <span class="value-primary">风速 ${this.windSpeedDisplay}</span>
                <span class="value-primary">风向 ${this.windDirectionDisplay}</span>
              </div>
            </div>
            <div class="item-status ${this.getStatusClass('wind')}">
              <span class="status-dot"></span>
            </div>
          </div>
        `
      }

      // 流速计信息 - 根据 shouldShowFlowmeter 判断是否显示
      if (this.shouldShowFlowmeter) {
        itemsHTML += `
          <div class="data-item">
            <div class="item-icon flowmeter-icon">
              <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
                <path d="M12.5 7H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
                <path d="M8 12l4-4 4 4-4 4z" opacity="0.5"/>
              </svg>
            </div>
            <div class="item-content">
              <div class="item-label">流速监测</div>
              <div class="item-values">
                <span class="value-primary">流速 ${this.flowVelocityDisplay}</span>
              </div>
            </div>
            <div class="item-status ${this.getStatusClass('flowmeter')}">
              <span class="status-dot"></span>
            </div>
          </div>
        `
      }

      return `
        <div class="panel-header">
          <span class="panel-title">实时监测</span>
          <button class="history-data-btn" type="button">
            <i class="el-icon-time"></i>
            <span>历史数据</span>
          </button>
        </div>
        
        <div class="panel-content">
          ${itemsHTML}
        </div>

        <div class="panel-footer">
          <span class="update-time">${this.lastUpdateDisplay}</span>
        </div>
      `
    },
    
    openHistoryDataPage() {
      // 参考"桥梁偏航统计"页面的实现方式，使用localStorage传递桥梁ID
      // 先保存当前桥梁ID到localStorage
      if (this.selectedBridgeId) {
        bridgeStateManager.setCurrentBridgeId(this.selectedBridgeId)
      }
      
      // 使用router.resolve生成URL，在新窗口打开
      if (this.$router) {
        const url = this.$router.resolve({ 
          path: '/data-management/history'
        }).href
        window.open(url, '_blank')
      } else {
        // 降级方案：如果router不可用，使用基础路径
        const baseUrl = window.location.origin
        const path = window.location.pathname.replace(/\/$/, '')
        const url = `${baseUrl}${path}/data-management/history`
        window.open(url, '_blank')
      }
    },
    
    getHistoryDataUrl() {
      // 已废弃：现在使用openHistoryDataPage方法直接打开页面
      // 保留此方法以防其他地方调用
      if (this.$router) {
        const url = this.$router.resolve({ 
          path: '/data-management/history'
        }).href
        return url
      }
      const baseUrl = window.location.origin
      const path = window.location.pathname.replace(/\/$/, '')
      return `${baseUrl}${path}/data-management/history`
    }
  },
  
  beforeDestroy() {
    this.stopPolling()
    this.destroyPanel()
    if (this.hideTimer) {
      clearTimeout(this.hideTimer)
    }
  }
}
</script>

<style scoped>
.hydrometeorological-indicator {
  display: inline-block;
  margin-left: 16px;
}

.indicator-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 6px;
  transition: all 0.2s ease;
  color: rgba(255, 255, 255, 0.9);
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.indicator-label:hover {
  background: rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 1);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.indicator-icon {
  margin-right: 6px;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2));
}

.indicator-text {
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  letter-spacing: 0.5px;
}

</style>

<!-- 全局样式用于悬浮面板 -->
<style>
.hydrometeorological-floating-panel {
  min-width: 320px;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border-radius: 12px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(226, 232, 240, 0.8);
  overflow: hidden;
  backdrop-filter: blur(8px);
  opacity: 0;
  transform: translateY(-8px);
  transition: all 0.2s ease;
}

.hydrometeorological-floating-panel.panel-visible {
  opacity: 1;
  transform: translateY(0);
}

.hydrometeorological-floating-panel .panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px 12px;
  background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
  color: white;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.hydrometeorological-floating-panel .header-icon {
  margin-right: 8px;
  opacity: 0.9;
}

.hydrometeorological-floating-panel .panel-title {
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.3px;
}

.hydrometeorological-floating-panel .history-data-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  color: white;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.hydrometeorological-floating-panel .history-data-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
  transform: translateY(-1px);
}

.hydrometeorological-floating-panel .history-data-btn i {
  font-size: 12px;
}

.hydrometeorological-floating-panel .panel-content {
  padding: 8px;
}

.hydrometeorological-floating-panel .data-item {
  display: flex;
  align-items: center;
  padding: 12px 10px;
  border-radius: 8px;
  margin-bottom: 6px;
  transition: all 0.2s ease;
  background: rgba(248, 250, 252, 0.6);
  border: 1px solid rgba(226, 232, 240, 0.5);
}

.hydrometeorological-floating-panel .data-item:hover {
  background: rgba(241, 245, 249, 0.8);
  transform: translateX(2px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.hydrometeorological-floating-panel .data-item:last-child {
  margin-bottom: 0;
}

.hydrometeorological-floating-panel .item-icon {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
}

.hydrometeorological-floating-panel .water-icon {
  background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%);
  color: white;
}

.hydrometeorological-floating-panel .visibility-icon {
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
  color: white;
}

.hydrometeorological-floating-panel .wind-icon {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
}

.hydrometeorological-floating-panel .flowmeter-icon {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
}

.hydrometeorological-floating-panel .item-content {
  flex: 1;
  min-width: 0;
}

.hydrometeorological-floating-panel .item-label {
  font-size: 12px;
  color: #64748b;
  margin-bottom: 4px;
  font-weight: 500;
}

.hydrometeorological-floating-panel .item-values {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.hydrometeorological-floating-panel .value-primary {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
}

.hydrometeorological-floating-panel .value-secondary {
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
}

.hydrometeorological-floating-panel .item-status {
  flex-shrink: 0;
  margin-left: 8px;
}

.hydrometeorological-floating-panel .status-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.8);
}

.hydrometeorological-floating-panel .status-normal .status-dot {
  background: #10b981;
  box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.2);
}

.hydrometeorological-floating-panel .status-error .status-dot {
  background: #ef4444;
  box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.2);
}

.hydrometeorological-floating-panel .status-disabled .status-dot {
  background: #9ca3af;
  box-shadow: 0 0 0 2px rgba(156, 163, 175, 0.2);
}

.hydrometeorological-floating-panel .panel-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 18px;
  background: rgba(248, 250, 252, 0.8);
  border-top: 1px solid rgba(226, 232, 240, 0.5);
}

.hydrometeorological-floating-panel .update-time {
  font-size: 11px;
  color: #64748b;
  font-weight: 500;
}

.hydrometeorological-floating-panel .refresh-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
  cursor: pointer;
  transition: all 0.2s ease;
}

.hydrometeorological-floating-panel .refresh-btn:hover:not(:disabled) {
  background: rgba(59, 130, 246, 0.2);
  transform: scale(1.05);
}

.hydrometeorological-floating-panel .refresh-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.hydrometeorological-floating-panel .animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
